import React, { createContext, PureComponent, ReactNode } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { languages } from '../../configs/env';
import { NOT_FOUND } from '../../configs/routes';
import { reportError } from '../../error-handling/reportError';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import template from '../../helper/template';
import { Maybe } from '../../types';
import FetchError from '../../utils/FetchError';
import { logger } from '../../utils/logger';
import { defer, Deferred } from '../../utils/defer';
import URLSearchParamsUtils from '../../utils/URLSearchParams';
import { withUrlLanguage, WithUrlLanguageProps } from '../UrlLanguageProvider/withUrlLanguage';
import * as Typo3Fetcher from './fetcher';
import { selectLabels } from './selectors';
import { Typo3State, Typo3StaticContent } from './types';
import { tryFetch, validateQueryParamsPredicate } from './utils';
import { tryIt } from '../../utils/tryIt';
import { BzitVehicle } from '../../bzit';

type ProviderProps = WithUrlLanguageProps & RouteComponentProps<any, any, any> & {
  children: ReactNode;
};

type ProviderState = Typo3State & {
  path: string;
  queryParams: URLSearchParams;
};

const defaultTypo3State: Typo3State = {
  staticContent: null,
  staticContentReady: Promise.resolve(null) as any,
  pathContent: null,
  error: null,
  languageUid: 0,
  translateTypo3: () => '',
  translateUsedVehicles: () => '',
  fetchPrivacyInfo: () => Promise.resolve(''),
  vehicleSlugs: {
    dateFetched: 0,
    slugs: {},
  },
  updateVehicleSlugs: async () => {},
  requireVehicleSlugs: async (vehicles) => vehicles,
};

export const Typo3Context = createContext(defaultTypo3State);

class Typo3Provider extends PureComponent<ProviderProps, ProviderState> {
  public static getDerivedStateFromProps(
    { location }: ProviderProps,
    { path }: ProviderState
  ): Partial<ProviderState> | null {
    if (location.pathname !== path) {
      return {
        path: location.pathname,
        queryParams: URLSearchParamsUtils.filter(
          new URLSearchParams(location.search),
          validateQueryParamsPredicate
        )
      };
    }
    return null;
  }

  private deferredStaticContentReady: Deferred<Typo3StaticContent> = defer();
  private vehicleSlugsArePending?: Promise<void> | undefined;

  public state: ProviderState = {
    staticContent: null,
    staticContentReady: this.deferredStaticContentReady.promise,
    pathContent: null,
    error: null,
    path: '',
    queryParams: new URLSearchParams(),
    translateTypo3: (
      label: string,
      data?: { [key: string]: Maybe<string> } | null,
      fallback?: string
    ) => this.translateTypo3(label, data, fallback),
    translateUsedVehicles: (
      label: string,
      data?: { [key: string]: Maybe<string> } | null,
      fallback?: string
    ) => this.translateUsedVehicles(label, data, fallback),
    languageUid: this.props.urlLanguage.languageUid,
    fetchPrivacyInfo: Typo3Fetcher.fetchPrivacyInfoCached,
    vehicleSlugs: {
      dateFetched: 0,
      slugs: {},
    },
    updateVehicleSlugs: this.updateVehicleSlugs.bind(this),
    requireVehicleSlugs: this.requireVehicleSlugs.bind(this),
  };

  private translate = (
    config: {
      provider: 'usedVehicles' | 'typo3',
      label: string;
      data?: { [key: string]: Maybe<string> } | null,
      fallback?: string
    }
  ) => {
    const {provider, label, data, fallback} = config;

    const labels = selectLabels(this.state)[provider];
    const labelContent = labels[label];
    if (!labelContent) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`🏳️‍🌈 ${provider} -> label: '${label}' not found`);
        fallback && console.warn(`🏳️‍🌈 ${provider} -> fallback: '${fallback}'`);
      }
      // don't simply check for empty, we want to provide an empty string fallback to skip the missing translation log
      if (typeof fallback !== 'undefined' && fallback !== null) {
        return data ? template(fallback, data) : fallback;
      }
      return `{${provider}: ${label}}`;
    }
    return data ? template(labelContent, data) : labelContent;
  };

  public translateTypo3 = (
    label: string,
    data?: { [key: string]: Maybe<string> } | null,
    fallback?: string
  ) => {
    return this.translate({label, data, fallback, provider: 'typo3'});
  };

  public translateUsedVehicles = (
    label: string,
    data?: { [key: string]: Maybe<string> } | null,
    fallback?: string
  ) => {
    return this.translate({label, data, fallback, provider: 'usedVehicles'});
  };

  public async fetchPathContent() {
    const { path, queryParams } = this.state;
    this.setState({
      pathContent: null
    });
    if (queryParams && queryParams.get('no_cache') === '1') {
      return Typo3Fetcher.fetchPathContent({
        path,
        additionalParams: queryParams
      });
    }
    return Typo3Fetcher.fetchPathContentCached({
      path,
      additionalParams: queryParams
    });
  }

  public async fetchStaticContent(languageUid: number) {
    const { queryParams } = this.state;
    if (queryParams && queryParams.get('no_cache') === '1') {
      return Typo3Fetcher.fetchStaticContent({
        languageUid,
        additionalParams: queryParams
      });
    }
    return Typo3Fetcher.fetchStaticContentCached({ languageUid });
  }

  public async updateContent() {
    const { path, staticContent, languageUid } = this.state;
    //console.time(`Typo3Provider.updateContent, ${path}`);
    try {
      const pathContent = await this.fetchPathContent();
      const languageUid =
        pathContent.sys_language_uid || pathContent.meta.sys_language_uid;
      if (!staticContent) {
        const newStaticContent = await tryFetch(
          this.fetchStaticContent(languageUid),
          e => {
            reportError('Typo3Provider.updateContent(#static)', e);
            return Typo3Fetcher.fetchStaticContent({
              languageUid,
              additionalParams: URLSearchParamsUtils.from({
                id: pathContent.meta.page.uid
              })
            });
          }
        );

        if (this.state.path === path) {
          this.setState({
            pathContent,
            languageUid,
            staticContent: newStaticContent
          });
          this.deferredStaticContentReady.resolve(newStaticContent);
          await this.updateVehicleSlugs();
        }
      } else {
        this.setState({ pathContent, languageUid });
      }
    } catch (error: any) {
      const { history } = this.props;
      if (error instanceof FetchError && error.response && error.response.ok && error.response.redirected) {
        // Follow T3 redirects in FE #51921
        logger.info('typo3', `Got redirect from ${path} to ${error.response.url}`);
        const redirectUrl = new URL(error.response.url).pathname;
        history.push(redirectUrl);

      } else {
        reportError('Typo3Provider.updateContent()', error);

        const { message, statusCode, stack } = error;
        const locale = languages[languageUid];
        const languageCode = locale ? locale.languageCode : 'de';
        history.push(`/${languageCode}/${NOT_FOUND}`, {
          error: { message, statusCode, stack }
        });
      }
    }
    //console.timeEnd(`Typo3Provider.updateContent, ${path}`);
  }

  async updateVehicleSlugs(maxAge: number = 15 * 60 * 1000) {
    if (
        this.vehicleSlugsArePending
        || (Date.now() - this.state.vehicleSlugs.dateFetched < maxAge)
      ) {
      return this.vehicleSlugsArePending;
    }

    const deferred = defer<void>();
    this.vehicleSlugsArePending = deferred.promise;

    const staticContent = await this.state.staticContentReady;

    const vehicleSlugs = await tryIt(fetch(`/${staticContent.meta.vehicleSlugsFile}?_=${Date.now()}`));
    if (!vehicleSlugs.ok) {
      reportError('Typo3Provider.updateContent(#vehicleSlugs)', vehicleSlugs.error);
      deferred.resolve();
      this.vehicleSlugsArePending = undefined;
      return;
    }

    type VehicleSlugsFile = {vehicleSlugs: Record<string, string | undefined>};
    const parsedVehicleSlugs: VehicleSlugsFile = await vehicleSlugs.result.json();

    this.setState({
      vehicleSlugs: {
        dateFetched: Date.now(),
        slugs: parsedVehicleSlugs.vehicleSlugs,
      },
    });

    deferred.resolve();
    this.vehicleSlugsArePending = undefined;
  }

  async requireVehicleSlugs<T extends BzitVehicle>(vehicles: T[]) {
    await this.updateVehicleSlugs();

    const missingVehicleMappingsStep1 = vehicles.filter((vehicle) => !this.state.vehicleSlugs.slugs[vehicle.vehicleId]);
    if (missingVehicleMappingsStep1.length) {
      missingVehicleMappingsStep1.forEach((vehicle) => {
        logger.debug('typo3', `Missing mapping for vehicle`, vehicle);
      });

      logger.debug('typo3', 'Retry getting missing bus mappings with reduced cache lifetime');
      await this.updateVehicleSlugs(60 * 1000);
    }

    const mappedVehicles = vehicles.filter((vehicle) => {
      if (!this.state.vehicleSlugs.slugs[vehicle.vehicleId]) {
        console.warn(`After refresh: Missing mapping for vehicle`, vehicle);
        return false;
      }

      return true;
    });

    return mappedVehicles;
  }

  public componentDidMount() {
    this.updateContent();
  }

  public componentDidUpdate(
    prevProps: ProviderProps,
    prevState: ProviderState
  ) {
    const { path } = this.state;
    if (path !== prevState.path) {
      this.updateContent();
    }
  }

  public render() {
    const { children } = this.props;
    return (
      <Typo3Context.Provider value={this.state}>
        {children}
      </Typo3Context.Provider>
    );
  }
}

export default withErrorBoundary(withRouter(withUrlLanguage(Typo3Provider)), 'Typo3Provider');
